import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronLeft, ChevronRight, Lightbulb, Baby, Pause, Play, RotateCcw } from "lucide-react";
import { FeatureShell, DemoPanel } from "../components/sections/FeatureShell";

const STEPS = [
  { t: "Prep the vegetables", d: "Cut zucchini, bell peppers and carrots into even, bite-sized pieces.", tip: "Even sizes cook evenly — no mushy zucchini next to crunchy carrots.", kid: "Wash the vegetables and tear the herbs.", time: 300 },
  { t: "Season the chicken", d: "Pat chicken dry, then coat with olive oil, minced garlic, salt, pepper and dried herbs.", tip: "Dry chicken browns beautifully; wet chicken steams.", kid: "Sprinkle the herbs from up high like snow.", time: 120 },
  { t: "Heat the pan", d: "Warm a large skillet over medium-high heat with a tablespoon of oil until it shimmers.", tip: "A hot pan is what gives you that golden crust.", kid: "Watch from a safe step back and say when it shimmers.", time: 90 },
  { t: "Sear the chicken", d: "Cook chicken 5–6 minutes per side until golden and cooked through.", tip: "Resist moving it — it releases on its own when ready.", kid: "Set the table while the grown-up flips.", time: 360 },
  { t: "Cook the vegetables", d: "Add vegetables to the same pan and stir-fry 4–5 minutes until bright and tender-crisp.", tip: "Leftover chicken juices add flavour for free.", kid: "Count the colours in the pan.", time: 270 },
  { t: "Plate and enjoy", d: "Slice the chicken, serve over the vegetables, and finish with lemon and fresh herbs.", tip: "Let chicken rest 3 minutes so it stays juicy.", kid: "Add the final herb garnish.", time: 180 },
];

const fmt = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;

const Timer = ({ seconds }) => {
  const [left, setLeft] = useState(seconds);
  const [run, setRun] = useState(false);
  useEffect(() => { setLeft(seconds); setRun(false); }, [seconds]);
  useEffect(() => {
    if (!run || left <= 0) return undefined;
    const t = setInterval(() => setLeft((l) => Math.max(0, l - 1)), 1000);
    return () => clearInterval(t);
  }, [run, left]);
  return (
    <div className="flex items-center gap-3 rounded-2xl border border-cyan-400/30 bg-deep/70 px-4 py-3">
      <span data-testid="cook-timer" className="font-mono text-2xl text-white">{fmt(left)}</span>
      <button onClick={() => setRun((r) => !r)} data-testid="cook-timer-toggle" aria-label="Start or pause timer" className="grid h-9 w-9 place-items-center rounded-full bg-cyan-400 text-ink">{run ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}</button>
      <button onClick={() => { setLeft(seconds); setRun(false); }} data-testid="cook-timer-reset" aria-label="Reset timer" className="grid h-9 w-9 place-items-center rounded-full border border-cyan-400/40 text-cyan-200"><RotateCcw className="h-4 w-4" /></button>
    </div>
  );
};

export default function Cooking() {
  const [i, setI] = useState(0);
  const s = STEPS[i];
  return (
    <FeatureShell slug="guided-cooking">
      <DemoPanel title="Garlic Herb Chicken & Vegetables" note="Demonstration only. A scripted sample recipe — no AI is guiding these steps.">
        <div className="grid gap-8 lg:grid-cols-12">
          <ol className="space-y-2 lg:col-span-4">
            {STEPS.map((st, j) => (
              <li key={st.t}>
                <button onClick={() => setI(j)} data-testid={`cook-step-${j}`} className={`flex w-full items-center gap-4 rounded-xl px-4 py-3 text-left transition-colors ${j === i ? "bg-cyan-400/15 text-white" : "text-slate-300 hover:bg-white/5"}`}>
                  <span className={`grid h-8 w-8 shrink-0 place-items-center rounded-full font-mono text-xs ${j < i ? "bg-cyan-400 text-ink" : j === i ? "border border-cyan-300 text-cyan-200" : "border border-white/20"}`}>{j + 1}</span>{st.t}
                </button>
              </li>
            ))}
          </ol>
          <div className="lg:col-span-8">
            <div className="mb-6 h-1.5 overflow-hidden rounded-full bg-white/10"><motion.div className="h-full bg-gradient-to-r from-cyan-300 to-blue-500" animate={{ width: `${((i + 1) / STEPS.length) * 100}%` }} transition={{ duration: 0.6 }} /></div>
            <div className="relative min-h-[340px]">
              <AnimatePresence initial={false}>
                <motion.div key={i} initial={{ opacity: 0, x: 40 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -40, position: "absolute" }} transition={{ duration: 0.45 }} className="inset-0 w-full" data-testid="cook-current-step">
                  <p className="font-mono text-sm text-cyan-300">STEP {i + 1} OF {STEPS.length}</p>
                  <h3 className="mt-3 font-display text-4xl font-semibold text-white">{s.t}</h3>
                  <p className="mt-4 text-lg text-slate-200">{s.d}</p>
                  <div className="mt-6 grid gap-3 sm:grid-cols-2">
                    <p className="flex gap-3 rounded-2xl bg-blue-500/10 p-4 text-sm text-slate-200"><Lightbulb className="h-5 w-5 shrink-0 text-cyan-300" />{s.tip}</p>
                    <p className="flex gap-3 rounded-2xl bg-blue-500/10 p-4 text-sm text-slate-200"><Baby className="h-5 w-5 shrink-0 text-cyan-300" />{s.kid}</p>
                  </div>
                  <div className="mt-6"><Timer seconds={s.time} /></div>
                </motion.div>
              </AnimatePresence>
            </div>
            <div className="mt-6 flex justify-between">
              <button disabled={i === 0} onClick={() => setI(i - 1)} data-testid="cook-prev-btn" className="btn-ghost disabled:opacity-40"><ChevronLeft className="h-4 w-4" /> Back</button>
              <button disabled={i === STEPS.length - 1} onClick={() => setI(i + 1)} data-testid="cook-next-btn" className="btn-primary disabled:opacity-40">Next step <ChevronRight className="h-4 w-4" /></button>
            </div>
          </div>
        </div>
      </DemoPanel>
    </FeatureShell>
  );
}
